import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  ShoppingCart, 
  TrendingUp,
  Receipt,
  AlertCircle,
  Plus,
  Search,
  Settings,
  Eye,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesPage = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodo, setPeriodo] = useState('hoy');
  const [estadoFilter, setEstadoFilter] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchSales = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (periodo) params.append('periodo', periodo);
      if (estadoFilter) params.append('estado_pago', estadoFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/sales?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  }, [token, periodo, estadoFilter, searchTerm]);

  useEffect(() => {
    fetchStats();
    fetchSales();
  }, [fetchStats, fetchSales]);

  const formatCurrency = (amount) => {
    return `Q ${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getStatusBadge = (estado) => {
    const styles = {
      PAGADA: 'bg-green-100 text-green-700 border-green-200',
      PARCIAL: 'bg-amber-100 text-amber-700 border-amber-200',
      PENDIENTE: 'bg-red-100 text-red-700 border-red-200',
      ANULADA: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    const labels = {
      PAGADA: 'Pagada',
      PARCIAL: 'Parcial',
      PENDIENTE: 'Pendiente',
      ANULADA: 'Anulada'
    };
    return (
      <Badge className={`${styles[estado] || 'bg-gray-100'} border`}>
        {labels[estado] || estado}
      </Badge>
    );
  };

  const canAccessAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  if (!canAccessAdmin) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Acceso Restringido</h2>
        <p className="text-slate-500 mt-2">Solo administradores pueden ver el dashboard de ventas.</p>
        <Link to="/admin/ventas/nueva">
          <Button className="mt-4 bg-[#0891B2]">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Venta
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]" style={{ fontFamily: 'Fraunces, serif' }}>
            Ventas
          </h1>
          <p className="text-slate-500 mt-1">Gestión de ventas y cobros</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/ventas/configuracion">
            <Button variant="outline" className="border-slate-200">
              <Settings className="h-4 w-4 mr-2" />
              Catálogo
            </Button>
          </Link>
          <Link to="/admin/ventas/nueva">
            <Button className="bg-[#0891B2] hover:bg-[#0e7490]" data-testid="new-sale-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Venta
            </Button>
          </Link>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ventas Hoy</p>
                  <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.ventas_hoy)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ventas Mes</p>
                  <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.ventas_mes)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <Receipt className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ticket Promedio</p>
                  <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.ticket_promedio_mes)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-50">
                  <ShoppingCart className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ventas Hoy</p>
                  <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {stats.cantidad_ventas_hoy}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Saldos Pend.</p>
                  <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.saldos_pendientes_total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <Tabs value={periodo} onValueChange={setPeriodo} className="w-full md:w-auto">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="hoy">Hoy</TabsTrigger>
                <TabsTrigger value="semana">Semana</TabsTrigger>
                <TabsTrigger value="mes">Mes</TabsTrigger>
                <TabsTrigger value="">Todas</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex gap-2 flex-1 w-full md:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por número o paciente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={estadoFilter}
                onChange={(e) => setEstadoFilter(e.target.value)}
                className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="">Todos los estados</option>
                <option value="PAGADA">Pagadas</option>
                <option value="PARCIAL">Parciales</option>
                <option value="PENDIENTE">Pendientes</option>
                <option value="ANULADA">Anuladas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Ventas ({sales.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-2">
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay ventas para mostrar</p>
              <Link to="/admin/ventas/nueva">
                <Button className="mt-4 bg-[#0891B2]">
                  <Plus className="h-4 w-4 mr-2" />
                  Crear primera venta
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Número</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Paciente</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Estado</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Método</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Atendido por</th>
                    <th className="text-center py-3 px-2 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr 
                      key={sale.id} 
                      className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                      data-testid={`sale-row-${sale.id}`}
                    >
                      <td className="py-3 px-2">
                        <span className="font-mono text-sm font-medium text-[#0B3B71]">
                          {sale.numero_venta}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {sale.fecha_venta}
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-sm font-medium text-slate-800">
                          {sale.nombre_paciente}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-sm font-semibold text-slate-800">
                          {formatCurrency(sale.total)}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {getStatusBadge(sale.estado_pago)}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {sale.metodo_pago_resumen || '-'}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {sale.atendido_por_nombre || '-'}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Link to={`/admin/ventas/${sale.id}`}>
                          <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0891B2]">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesPage;
